/**
 * Analytics Dashboard Section
 *
 * Shared analytics renderer used by:
 * - the legacy /#/analytics route (full page)
 * - the merged Dashboard page section
 *
 * @package MultilingualPressZone
 */

import Chart from 'chart.js/auto';
import { el, __, clear, formatNumber } from '../utils/dom.js';
import API from '../utils/api.js';
import Card from '../components/Card.js';
import { Button } from '../components/Button.js';
import { Spinner } from '../components/Spinner.js';
import { Notice } from '../components/Notice.js';
import { PageHeader } from '../components/PageHeader.js';

export default class AnalyticsDashboard {
    /**
     * @param {HTMLElement} container
     * @param {Object} options
     * @param {'page'|'dashboard'} [options.layout]
     * @param {boolean} [options.showHeader]
     */
    constructor(container, options = {}) {
        this.container = container;
        this.charts = {};

        this.layout = options.layout || 'page';
        this.showHeader = typeof options.showHeader === 'boolean' ? options.showHeader : (this.layout === 'page');

        const cfg = window.mpzAnalytics || {};
        this.ajaxUrl = cfg.ajaxUrl || '/wp-admin/admin-post.php';
        this.exportNonce = cfg.exportNonce || '';
        this.labels = cfg.labels || {};

        this.noticeContainer = null;
        this.activityCanvas = null;
        this.coverageChartWrap = null;
        this.coverageCanvas = null;
        this.velocityCanvas = null;
        this.healthContainer = null;
        this.velocitySummary = null;
    }

    /**
     * Render the analytics dashboard into this.container.
     */
    async render() {
        clear(this.container);

        if (this.showHeader) {
            const header = PageHeader({
                title: __('Analytics Dashboard', 'multilingual-press-zone'),
                description: __('Translation coverage, health, and velocity.', 'multilingual-press-zone'),
                className: 'mpz-page-header--analytics',
                actions: [
                    Button({
                        label: __('Refresh', 'multilingual-press-zone'),
                        variant: 'secondary',
                        icon: '\u21BB',
                        onClick: () => this.loadData(true),
                        attrs: { id: 'mpz-refresh-analytics' }
                    })
                ]
            });
            this.container.appendChild(header);
        } else {
            this.container.appendChild(this.renderDashboardSectionHeader());
        }

        this.noticeContainer = el('div', { class: 'mpz-analytics-notices' });
        this.container.appendChild(this.noticeContainer);

        // Card 1: Translation Activity
        this.activityCanvas = el('canvas', { id: 'mpz-chart-activity' });

        const activityCard = Card('\uD83D\uDCCA', __('Translation Activity', 'multilingual-press-zone'), __('Monthly content and string translations.', 'multilingual-press-zone'), [
            this.renderExportLink('volume', this.labels.exportVolume || __('Export CSV', 'multilingual-press-zone')),
            el('div', { class: 'mpz-analytics-chart mpz-analytics-chart--activity' }, this.activityCanvas)
        ]);

        // Card 2: Language Coverage
        this.coverageCanvas = el('canvas', { id: 'mpz-chart-coverage' });
        this.coverageChartWrap = el('div', { class: 'mpz-analytics-chart' }, this.coverageCanvas);

        const coverageCard = Card('\uD83C\uDF10', __('Language Coverage', 'multilingual-press-zone'), __('Translation coverage per language.', 'multilingual-press-zone'), [
            this.renderExportLink('coverage', __('Export CSV', 'multilingual-press-zone')),
            this.coverageChartWrap
        ]);

        // Card 3: Translation Health
        this.healthContainer = el('div', { class: 'mpz-analytics-health' },
            el('div', { class: 'presszone-multilingual-loading' },
                Spinner({ size: 'md', label: __('Loading...', 'multilingual-press-zone') })
            )
        );

        const healthCard = Card('\uD83D\uDCCA', __('Translation Health', 'multilingual-press-zone'), __('Status distribution across content and strings.', 'multilingual-press-zone'), [
            this.renderExportLink('health', __('Export CSV', 'multilingual-press-zone')),
            this.healthContainer
        ]);

        // Card 4: Translation Velocity
        this.velocitySummary = el('div', { class: 'mpz-analytics-velocity-summary' });
        this.velocityCanvas = el('canvas', { id: 'mpz-chart-velocity' });

        const velocityCard = Card('\uD83D\uDE80', __('Translation Velocity', 'multilingual-press-zone'), __('Daily completions with 7-day trend.', 'multilingual-press-zone'), [
            this.renderExportLink('velocity', __('Export CSV', 'multilingual-press-zone')),
            this.velocitySummary,
            el('div', { class: 'mpz-analytics-chart mpz-analytics-chart--velocity' }, this.velocityCanvas)
        ], { staggerIndex: 3 });

        const grid = this.renderGrid({
            activityCard,
            coverageCard,
            healthCard,
            velocityCard
        });

        this.container.appendChild(grid);

        await this.loadData(false);
    }

    renderDashboardSectionHeader() {
        return el('div', { class: 'presszone-multilingual-dashboard__section-header' },
            el('div', { class: 'presszone-multilingual-dashboard__section-header-text' },
                el('h2', { class: 'presszone-multilingual-dashboard__section-title' }, __('Analytics', 'multilingual-press-zone')),
                el('p', { class: 'presszone-multilingual-dashboard__section-subtitle' }, __('Coverage, health, and velocity.', 'multilingual-press-zone'))
            ),
            el('div', { class: 'presszone-multilingual-dashboard__section-header-actions' },
                Button({
                    label: __('Refresh', 'multilingual-press-zone'),
                    variant: 'secondary',
                    icon: '\u21BB',
                    onClick: () => this.loadData(true),
                    attrs: { id: 'mpz-refresh-analytics' }
                })
            )
        );
    }

    renderGrid({ activityCard, coverageCard, healthCard, velocityCard }) {
        if (this.layout === 'dashboard') {
            return el('div', { class: 'presszone-multilingual-dashboard__analytics-grid' },
                el('div', { class: 'presszone-multilingual-dashboard__analytics-item presszone-multilingual-dashboard__analytics-item--activity' }, activityCard),
                el('div', { class: 'presszone-multilingual-dashboard__analytics-item presszone-multilingual-dashboard__analytics-item--coverage' }, coverageCard),
                el('div', { class: 'presszone-multilingual-dashboard__analytics-item presszone-multilingual-dashboard__analytics-item--health' }, healthCard),
                el('div', { class: 'presszone-multilingual-dashboard__analytics-item presszone-multilingual-dashboard__analytics-item--velocity' }, velocityCard)
            );
        }

        return el('div', { class: 'mpz-analytics-grid' }, activityCard, coverageCard, healthCard, velocityCard);
    }

    renderExportLink(type, label) {
        const url = this.buildExportUrl(type);
        return el('div', { class: 'mpz-analytics-card-actions' },
            el('a', {
                class: 'presszone-multilingual-btn presszone-multilingual-btn--ghost presszone-multilingual-btn--sm',
                href: url,
                target: '_blank',
                rel: 'noopener noreferrer'
            }, label)
        );
    }

    buildExportUrl(type) {
        const base = String(this.ajaxUrl || '/wp-admin/admin-post.php');
        const url = new URL(base, window.location.origin);
        url.searchParams.set('action', 'mpz_export_report');
        url.searchParams.set('type', String(type || ''));
        if (this.exportNonce) {
            url.searchParams.set('nonce', this.exportNonce);
        }
        return url.toString();
    }

    async loadData(force = false) {
        void force;

        const results = await Promise.allSettled([
            this.loadActivityChart(),
            this.loadCoverageChart(),
            this.loadTranslationHealth(),
            this.loadVelocityChart()
        ]);

        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length > 0) {
            this.renderNonFatalNotice(
                __('Some analytics data could not be loaded. Please refresh and try again.', 'multilingual-press-zone')
            );
        } else {
            this.clearNotices();
        }
    }

    clearNotices() {
        if (!this.noticeContainer) return;
        clear(this.noticeContainer);
    }

    renderNonFatalNotice(message) {
        if (!this.noticeContainer) return;
        if (this.noticeContainer.querySelector('.mpz-notice')) return;

        this.noticeContainer.appendChild(
            Notice({
                message,
                variant: 'warning',
                alert: true
            })
        );
    }

    async fetchJson(endpoint) {
        return await API.get(endpoint);
    }

    // =========================================================================
    // Card 1: Translation Activity
    // =========================================================================

    async loadActivityChart() {
        const result = await this.fetchJson('/analytics/volume');
        if (!result || !result.success) {
            throw new Error('Activity data not available');
        }

        if (this.charts.activity) {
            this.charts.activity.destroy();
        }

        this.charts.activity = new Chart(this.activityCanvas, {
            type: 'bar',
            data: result.data,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: __('Translations', 'multilingual-press-zone') }
                    },
                    y1: {
                        beginAtZero: true,
                        position: 'right',
                        title: { display: true, text: __('Characters', 'multilingual-press-zone') },
                        grid: { drawOnChartArea: false }
                    }
                }
            }
        });
    }

    // =========================================================================
    // Card 2: Language Coverage
    // =========================================================================

    async loadCoverageChart() {
        const result = await this.fetchJson('/analytics/coverage');
        if (!result || !result.success) {
            throw new Error('Coverage data not available');
        }

        const items = Array.isArray(result.data) ? result.data : [];

        if (this.charts.coverage) {
            this.charts.coverage.destroy();
            this.charts.coverage = null;
        }

        if (items.length === 0) {
            clear(this.coverageChartWrap);
            this.coverageChartWrap.appendChild(
                el('p', { class: 'mpz-analytics-muted' }, __('No active non-default languages configured.', 'multilingual-press-zone'))
            );
            return;
        }

        const labels = items.map((i) => i.language + ' (' + i.code + ')');
        const values = items.map((i) => i.coverage);
        const colors = values.map((v) => {
            if (v >= 80) return 'rgba(16, 185, 129, 0.7)';   // green
            if (v >= 50) return 'rgba(245, 158, 11, 0.7)';   // yellow
            return 'rgba(239, 68, 68, 0.7)';                  // red
        });
        const borderColors = values.map((v) => {
            if (v >= 80) return '#10b981';
            if (v >= 50) return '#f59e0b';
            return '#ef4444';
        });

        this.charts.coverage = new Chart(this.coverageCanvas, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    label: __('Coverage %', 'multilingual-press-zone'),
                    data: values,
                    backgroundColor: colors,
                    borderColor: borderColors,
                    borderWidth: 1
                }]
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        beginAtZero: true,
                        max: 100,
                        title: { display: true, text: '%' }
                    }
                }
            }
        });
    }

    // =========================================================================
    // Card 3: Translation Health
    // =========================================================================

    async loadTranslationHealth() {
        const result = await this.fetchJson('/analytics/translation-health');
        if (!result || !result.success) {
            throw new Error('Translation health not available');
        }

        const data = result.data || {};
        const contentStatuses = Array.isArray(data.content) ? data.content : [];
        const stringStatuses = Array.isArray(data.strings) ? data.strings : [];
        const warnings = Array.isArray(data.warnings) ? data.warnings : [];

        clear(this.healthContainer);

        if (contentStatuses.length === 0 && stringStatuses.length === 0) {
            this.healthContainer.appendChild(
                el('p', { class: 'mpz-analytics-muted' }, __('No translation data available.', 'multilingual-press-zone'))
            );
            return;
        }

        // Content statuses section
        if (contentStatuses.length > 0) {
            this.healthContainer.appendChild(
                el('div', { class: 'mpz-analytics-health-section' },
                    el('h4', { class: 'mpz-analytics-health-section__title' }, __('Content Translations', 'multilingual-press-zone')),
                    el('div', { class: 'mpz-analytics-workflow-grid' },
                        ...contentStatuses.map((s) => this.renderStatusItem(s.status, s.count))
                    )
                )
            );
        }

        // String statuses section
        if (stringStatuses.length > 0) {
            this.healthContainer.appendChild(
                el('div', { class: 'mpz-analytics-health-section' },
                    el('h4', { class: 'mpz-analytics-health-section__title' }, __('String Translations', 'multilingual-press-zone')),
                    el('div', { class: 'mpz-analytics-workflow-grid' },
                        ...stringStatuses.map((s) => this.renderStatusItem(s.status, s.count))
                    )
                )
            );
        }

        // Warning alerts
        if (warnings.length > 0) {
            const alertText = warnings.map((w) =>
                w.language + ' (' + w.code + '): ' + w.coverage + '% ' + __('coverage', 'multilingual-press-zone')
            ).join('; ');

            this.healthContainer.appendChild(
                el('div', { class: 'mpz-analytics-bottleneck-alert' },
                    el('strong', {}, __('Low Coverage Warning:', 'multilingual-press-zone') + ' '),
                    alertText
                )
            );
        }
    }

    renderStatusItem(status, count) {
        const stateClass = 'mpz-analytics-workflow-item mpz-analytics-workflow-item--' + this.sanitizeStateClass(status);
        return el('div', { class: stateClass },
            el('div', { class: 'mpz-analytics-workflow-item__state' }, this.formatStateName(status)),
            el('div', { class: 'mpz-analytics-workflow-item__count' }, formatNumber(count))
        );
    }

    sanitizeStateClass(state) {
        return String(state || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '-');
    }

    formatStateName(state) {
        return String(state || '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // =========================================================================
    // Card 4: Translation Velocity
    // =========================================================================

    async loadVelocityChart() {
        const result = await this.fetchJson('/analytics/velocity');
        if (!result || !result.success) {
            throw new Error('Velocity data not available');
        }

        const data = result.data || {};
        const daily = Array.isArray(data.daily) ? data.daily : [];
        const summary = data.summary || {};

        // Render summary stats
        clear(this.velocitySummary);

        const trendIcon = summary.trend_pct >= 0 ? '\u2191' : '\u2193';
        const trendClass = summary.trend_pct >= 0 ? 'mpz-analytics-velocity-stat--up' : 'mpz-analytics-velocity-stat--down';

        this.velocitySummary.appendChild(
            el('div', { class: 'mpz-analytics-velocity-stat' },
                el('div', { class: 'mpz-analytics-velocity-stat__label' }, __('Avg/day', 'multilingual-press-zone')),
                el('div', { class: 'mpz-analytics-velocity-stat__value' }, String(summary.avg_per_day || 0))
            )
        );
        this.velocitySummary.appendChild(
            el('div', { class: 'mpz-analytics-velocity-stat' },
                el('div', { class: 'mpz-analytics-velocity-stat__label' }, __('Total this period', 'multilingual-press-zone')),
                el('div', { class: 'mpz-analytics-velocity-stat__value' }, formatNumber(summary.total || 0))
            )
        );
        this.velocitySummary.appendChild(
            el('div', { class: 'mpz-analytics-velocity-stat ' + trendClass },
                el('div', { class: 'mpz-analytics-velocity-stat__label' }, __('Trend', 'multilingual-press-zone')),
                el('div', { class: 'mpz-analytics-velocity-stat__value' }, trendIcon + ' ' + Math.abs(summary.trend_pct || 0) + '%')
            )
        );

        // Render chart
        if (this.charts.velocity) {
            this.charts.velocity.destroy();
        }

        if (daily.length === 0) {
            return;
        }

        const labels = daily.map((d) => d.date);
        const completed = daily.map((d) => d.completed);
        const movingAvg = daily.map((d) => d.moving_avg);

        this.charts.velocity = new Chart(this.velocityCanvas, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: __('Completed', 'multilingual-press-zone'),
                        data: completed,
                        backgroundColor: 'rgba(52, 152, 219, 0.5)',
                        borderColor: '#3498db',
                        borderWidth: 1,
                        order: 2
                    },
                    {
                        type: 'line',
                        label: __('7-day Average', 'multilingual-press-zone'),
                        data: movingAvg,
                        borderColor: '#e74c3c',
                        backgroundColor: 'transparent',
                        borderWidth: 2,
                        pointRadius: 0,
                        tension: 0.3,
                        order: 1
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { beginAtZero: true }
                }
            }
        });
    }

    destroy() {
        Object.values(this.charts).forEach((c) => {
            try {
                c?.destroy?.();
            } catch (e) {
                // ignore
            }
        });
        this.charts = {};
        clear(this.container);

        this.noticeContainer = null;
        this.activityCanvas = null;
        this.coverageChartWrap = null;
        this.coverageCanvas = null;
        this.velocityCanvas = null;
        this.healthContainer = null;
        this.velocitySummary = null;
    }
}
